import { useMemo } from "react";
import isEmpty from "lodash/isEmpty";

import LineItemCard from "@sharedComponents/cart/line-item-card";

import {
  getUpdatedLineItemFromVariants,
  getCartProductType,
  groupItemsByShippedSeparately,
} from "@helpers/orders/cart";
import { isSwagProduct } from "@stadium/helpers/src/product/utils";
import { getProductAllVariantImages } from "@helpers/product-details";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";

import { SHIPPED_TOGETHER } from "@constants/orders/cart";

import styles from "../main.module.scss";

const emptyArr = [];

const CartProductListing = ({
  lineItems,
  productSets,
  medleySets,
  commonProps: {
    editable = false,
    disabled,
    removeProductFromCart,
    onProductChangeQuantity,
    variantsMapping,
    hidePrices = false,
    orderCurrencyCode,
    hideLink = false,
  } = {},
}) => {
  const { isSendToOthers } = useSendItemsContext();
  const { isGuestOrPreviewMode } = usePreviewModeContext();

  const updatedProductsWithVariant = (item) => {
    if (isGuestOrPreviewMode) {
      const variantImages = getProductAllVariantImages(item.product?.variantsIncludingMaster);
      const itemImages = isEmpty(item.images) ? variantImages : item.images;
      return {
        ...item,
        masterVariant: item.product?.masterVariant,
        isSwagVariantSelectionAvailable: item?.product?.variantsIncludingMaster?.length > 1,
        images: isSendToOthers && isSwagProduct(item.intent) ? variantImages : itemImages,
      };
    }
    return getUpdatedLineItemFromVariants(item, variantsMapping, isSendToOthers);
  };

  const updatedLineItemsData = useMemo(
    () => lineItems?.map(updatedProductsWithVariant),
    [lineItems],
  );

  const updatedProductSetsData = useMemo(
    () => productSets?.map(updatedProductsWithVariant),
    [productSets],
  );
  const updatedMedleySetsData = useMemo(
    () => medleySets?.map(updatedProductsWithVariant),
    [medleySets],
  );
  const renderProducts = (lineItems = emptyArr) => {
    return lineItems.map((item) => {
      const updatedItem = updatedProductsWithVariant(item);
      return (
        <LineItemCard
          key={`product-${item.id}-${item?.productId}`}
          intent={getCartProductType(item.type)}
          editable={editable}
          disabled={disabled}
          lineItem={updatedItem}
          removeProductFromCart={removeProductFromCart}
          onChangeQuantity={onProductChangeQuantity}
          getUpdatedItem={updatedProductsWithVariant}
          hidePrices={hidePrices}
          orderCurrencyCode={orderCurrencyCode}
          hideLink={hideLink}
          isCustomizable={!!item?.digitalAssetsData}
        />
      );
    });
  };

  const allAvailableProducts = [
    ...(updatedLineItemsData || emptyArr),
    ...(updatedProductSetsData || emptyArr),
    ...(updatedMedleySetsData || emptyArr),
  ];
  const { groupedProducts, bothCategoriesPresent } =
    groupItemsByShippedSeparately(allAvailableProducts);

  return Object.keys(groupedProducts).map((key) => {
    const productList = groupedProducts[key];

    if (isEmpty(productList)) return;
    return (
      <dl className={styles.dl} key={key}>
        {bothCategoriesPresent && (
          <dt className={styles.brandHeading}>
            {key === SHIPPED_TOGETHER ? "Ships From Stadium Warehouse" : "Ships Separately"}:
          </dt>
        )}
        {renderProducts(productList)}
      </dl>
    );
  });
};

export default CartProductListing;
